/**
 * DropDetailsView - View for managing drops for a trip
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import DropCard from "../components/DropCard";
import DropCompletionModal from "../components/DropCompletionModal";
import CreateDropModal from "../components/CreateDropModal";
import EditDropModal from "../components/EditDropModal";
import OrderStatusSelectionModal from "../components/OrderStatusSelectionModal";
import UpdateDeliveryProgressModal from "../components/UpdateDeliveryProgressModal";
import {
  getDropsByTrip,
  completeDrop,
  deleteDrop as deleteDropService,
} from "../services/dropService";
import {
  getOrdersByTrip,
  getOrder,
  updateOrderStatus,
} from "../../orders/services/orderService";
import { getTrip } from "../../trips/services/tripService";
import { logError, logInfo } from "../../../shared/utils/logger.js";

const DropDetailsView = () => {
  const { tripId } = useParams();
  const navigate = useNavigate();

  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trip, setTrip] = useState(null);
  const [selectedDrop, setSelectedDrop] = useState(null);
  const [completionModalOpen, setCompletionModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedDropForEdit, setSelectedDropForEdit] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [orderStatusModalOpen, setOrderStatusModalOpen] = useState(false);
  const [affectedOrders, setAffectedOrders] = useState([]);
  const [updatingStatuses, setUpdatingStatuses] = useState(false);
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [selectedDropForProgress, setSelectedDropForProgress] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchTrip = useCallback(async () => {
    if (!tripId) return;

    try {
      const tripData = await getTrip(tripId);
      setTrip(tripData);
      logInfo("Trip fetched successfully", {
        tripId,
        driverId: tripData?.driverId,
        driverName: tripData?.driver?.name,
      });
    } catch (err) {
      logError("Failed to fetch trip", {
        tripId,
        error: err.message,
      });
      // Don't set error state, just log - trip fetch failure shouldn't block drops view
    }
  }, [tripId]);

  const fetchDrops = useCallback(async () => {
    if (!tripId) return;

    try {
      setLoading(true);
      setError(null);

      const dropsData = await getDropsByTrip(tripId);
      setDrops(dropsData || []);
      logInfo("Drops fetched successfully", {
        tripId,
        count: dropsData?.length || 0,
      });
    } catch (err) {
      logError("Failed to fetch drops", {
        tripId,
        error: err.message,
      });
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => {
    fetchTrip();
    fetchDrops();
  }, [fetchTrip, fetchDrops]);

  const handleComplete = async (dropId, confirmationData) => {
    try {
      setCompleting(true);
      const result = await completeDrop(dropId, confirmationData);
      
      // Close completion modal
      setCompletionModalOpen(false);
      setSelectedDrop(null);
      
      // Refresh drops list
      await fetchDrops();
      
      // Extract order IDs from the completed drop (not all trip orders)
      if (result && result.orders && result.orders.length > 0) {
        try {
          // Extract order IDs from drop.orders array
          const orderIds = result.orders.map(order => 
            typeof order === 'string' ? order : order.orderId
          );
          
          // Fetch only the orders that are part of this drop
          const orderPromises = orderIds.map(orderId => 
            getOrder(orderId).catch(err => {
              logError("Failed to fetch order for drop", {
                orderId,
                dropId,
                error: err.message,
              });
              return null;
            })
          );
          
          const orders = (await Promise.all(orderPromises)).filter(Boolean);
          
          if (orders.length > 0) {
            setAffectedOrders(orders);
            setOrderStatusModalOpen(true);
            logInfo("Drop completed, showing order status modal", {
              dropId,
              tripId,
              ordersCount: orders.length,
              orderIds,
            });
          } else {
            // No orders found, just show success notification
            setNotification({
              open: true,
              message: "Drop completed successfully",
              severity: "success",
            });
          }
        } catch (orderError) {
          logError("Failed to fetch orders for drop", {
            dropId,
            error: orderError.message,
          });
          // Still show success for drop completion
          setNotification({
            open: true,
            message: "Drop completed successfully",
            severity: "success",
          });
        }
      } else {
        // No orders in this drop
        setNotification({
          open: true,
          message: "Drop completed successfully",
          severity: "success",
        });
      }
    } catch (err) {
      logError("Failed to complete drop", {
        dropId,
        error: err.message,
      });
      setNotification({
        open: true,
        message: "Failed to complete drop: " + err.message,
        severity: "error",
      });
    } finally {
      setCompleting(false);
    }
  };

  const handleOrderStatusUpdate = async (statusUpdates) => {
    try {
      setUpdatingStatuses(true);
      
      // Check if there are any changes
      if (!statusUpdates || Object.keys(statusUpdates).length === 0) {
        setNotification({
          open: true,
          message: "No status changes detected",
          severity: "info",
        });
        setOrderStatusModalOpen(false);
        setAffectedOrders([]);
        return;
      }

      const updatePromises = [];
      const errors = [];

      // Update each order status (only changed ones are passed)
      for (const [orderId, status] of Object.entries(statusUpdates)) {
        try {
          await updateOrderStatus(orderId, status);
          updatePromises.push({ orderId, status, success: true });
          logInfo("Order status updated", { orderId, status });
        } catch (orderError) {
          errors.push({ orderId, status, error: orderError.message });
          logError("Failed to update order status", {
            orderId,
            status,
            error: orderError.message,
          });
        }
      }

      // Show notification based on results
      if (errors.length === 0) {
        setNotification({
          open: true,
          message: `Successfully updated ${updatePromises.length} order status${updatePromises.length > 1 ? "es" : ""}`,
          severity: "success",
        });
      } else if (updatePromises.length > 0) {
        setNotification({
          open: true,
          message: `Updated ${updatePromises.length} order status${updatePromises.length > 1 ? "es" : ""}, ${errors.length} failed`,
          severity: "warning",
        });
      } else {
        setNotification({
          open: true,
          message: "Failed to update order statuses",
          severity: "error",
        });
      }

      setOrderStatusModalOpen(false);
      setAffectedOrders([]);
    } catch (err) {
      logError("Error updating order statuses", {
        error: err.message,
        stack: err.stack,
      });
      setNotification({
        open: true,
        message: "Failed to update order statuses: " + err.message,
        severity: "error",
      });
    } finally {
      setUpdatingStatuses(false);
    }
  };

  const handleEdit = (drop) => {
    setSelectedDropForEdit(drop);
    setEditModalOpen(true);
  };

  const handleEditSuccess = async () => {
    setEditModalOpen(false);
    setSelectedDropForEdit(null);
    setNotification({
      open: true,
      message: "Drop updated successfully",
      severity: "success",
    });
    // Refresh drops to show updated data
    await fetchDrops();
  };

  const handleUpdateProgress = (drop) => {
    setSelectedDropForProgress(drop);
    setProgressModalOpen(true);
  };

  const handleProgressUpdateSuccess = async () => {
    setProgressModalOpen(false);
    setSelectedDropForProgress(null);
    setNotification({
      open: true,
      message: "Delivery progress updated successfully",
      severity: "success",
    });
    // Refresh drops to show updated progress
    await fetchDrops();
  };

  const handleDelete = async (drop) => {
    if (!window.confirm(`Are you sure you want to delete drop at ${drop.location?.city}?`)) {
      return;
    }

    try {
      await deleteDropService(drop.id);
      setNotification({
        open: true,
        message: "Drop deleted successfully",
        severity: "success",
      });
      fetchDrops();
    } catch (err) {
      logError("Failed to delete drop", {
        dropId: drop.id,
        error: err.message,
      });
      setNotification({
        open: true,
        message: "Failed to delete drop: " + err.message,
        severity: "error",
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  if (loading && drops.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Drops for Trip {tripId}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchDrops}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            color="primary"
            onClick={() => setCreateModalOpen(true)}
          >
            New Drop
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {drops.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <Typography color="textSecondary">
                No drops found for this trip. Create a drop to get started.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Box>
          {drops.map((drop) => (
            <DropCard
              key={drop.id}
              drop={drop}
              onEdit={handleEdit}
              onUpdateProgress={handleUpdateProgress}
              onComplete={(drop) => {
                setSelectedDrop(drop);
                setCompletionModalOpen(true);
              }}
              onDelete={handleDelete}
            />
          ))}
        </Box>
      )}

      <DropCompletionModal
        open={completionModalOpen}
        drop={selectedDrop}
        trip={trip}
        onComplete={handleComplete}
        onCancel={() => {
          setCompletionModalOpen(false);
          setSelectedDrop(null);
        }}
        loading={completing}
      />

      <CreateDropModal
        open={createModalOpen}
        tripId={tripId}
        onSuccess={(drop) => {
          setCreateModalOpen(false);
          setNotification({
            open: true,
            message: "Drop created successfully",
            severity: "success",
          });
          fetchDrops();
        }}
        onCancel={() => setCreateModalOpen(false)}
      />

      <EditDropModal
        open={editModalOpen}
        drop={selectedDropForEdit}
        tripId={tripId}
        onSuccess={handleEditSuccess}
        onCancel={() => {
          setEditModalOpen(false);
          setSelectedDropForEdit(null);
        }}
      />

      <OrderStatusSelectionModal
        open={orderStatusModalOpen}
        orders={affectedOrders}
        onConfirm={handleOrderStatusUpdate}
        onCancel={() => {
          setOrderStatusModalOpen(false);
          setAffectedOrders([]);
        }}
        loading={updatingStatuses}
      />

      <UpdateDeliveryProgressModal
        open={progressModalOpen}
        drop={selectedDropForProgress}
        onSuccess={handleProgressUpdateSuccess}
        onCancel={() => {
          setProgressModalOpen(false);
          setSelectedDropForProgress(null);
        }}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DropDetailsView;

